// Iter 82.2 / Phase E1.2 — Leave Request Detail.
// Surfaces full request lifecycle + audit:
//  • Header (employee · type · dates · days · status badge)
//  • Workflow timeline (snapshot frozen at submit)
//  • Approval actions (Approve / Reject / Clarify) when caller is the
//    next-stage approver or HR
//  • Resubmit action when status = Clarification Requested
//  • Cancel action for owner (future-dated) or HR
//  • Team calendar awareness (direct reports + dept peers overlap)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, CheckCircle, XCircle, ChatCircle, ArrowsClockwise,
    PaperPlaneTilt, Stop, ClockCounterClockwise, Users, CalendarBlank, Warning,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const STATUS_TONE = {
    Draft: "bg-slate-100 text-slate-700",
    Pending: "bg-amber-100 text-amber-800",
    "Clarification Requested": "bg-sky-100 text-sky-800",
    Approved: "bg-emerald-100 text-emerald-800",
    Rejected: "bg-rose-100 text-rose-800",
    Cancelled: "bg-zinc-100 text-zinc-700",
    "Cancelled by HR": "bg-zinc-100 text-zinc-700",
};

const ACTION_TONE = {
    approve: "text-emerald-700",
    reject: "text-rose-700",
    clarify: "text-sky-700",
    resubmit: "text-amber-700",
};

export default function LeaveRequestDetail() {
    const { rid } = useParams();
    const navigate = useNavigate();
    const { user, can } = useAuth();
    const [req, setReq] = useState(null);
    const [me, setMe] = useState(null);
    const [team, setTeam] = useState(null);
    const [actionDraft, setActionDraft] = useState(null); // {kind, comments}

    const isHr = can("leave:admin") || user?.role === "super_admin";

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get(`/leave/request/${rid}`);
            setReq(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load request"));
        }
    }, [rid]);

    const loadMe = useCallback(async () => {
        try {
            const { data } = await api.get("/hr/ess/me");
            setMe(data);
        } catch {
            setMe(null);
        }
    }, []);

    const loadTeam = useCallback(async (r) => {
        if (!r) return;
        try {
            const { data } = await api.get("/leave/request/team-calendar", {
                params: { from_date: r.from_date, to_date: r.to_date, request_id: r.id },
            });
            setTeam(data);
        } catch {
            setTeam(null);
        }
    }, []);

    useEffect(() => { Promise.resolve().then(reload); }, [reload]);
    useEffect(() => { Promise.resolve().then(loadMe); }, [loadMe]);
    useEffect(() => { if (req) loadTeam(req); }, [req, loadTeam]);

    const isOwner = req && me && req.employee_id === me.id;
    const currentStage = useMemo(() => {
        if (!req || !req.workflow_snapshot) return null;
        return req.workflow_snapshot.stages.find((s) => s.id === req.current_stage_id);
    }, [req]);

    // Iter 109 Sanity Batch · Backend-authoritative permissions
    // (B-01/B-06/B-07). The UI consumes `caller_permissions` verbatim
    // from the server. NO client-side re-derivation. If the field is
    // missing (older backend), fall back to closed-safe defaults so
    // the UI never shows approver actions on ambiguity.
    const perms = req?.caller_permissions || {
        is_owner: isOwner, is_hr: isHr, is_current_approver: false,
        current_stage_role: null, can_act_on_stage: false,
        can_resubmit: false, can_cancel: false, can_submit_draft: false,
    };
    const canActOnStage   = !!perms.can_act_on_stage;
    const canResubmit     = !!perms.can_resubmit;
    const canCancel       = !!perms.can_cancel;
    const canSubmitDraft  = !!perms.can_submit_draft;
    const isCurrentApprover = !!perms.is_current_approver;
    // Persona-is-context (B-01/B-02) — the caller is an "approver
    // context" ONLY when they are NOT the applicant AND they either
    // are the routed current approver OR are HR-with-override on a
    // pending stage. Applicants NEVER see the approver console.
    const inApproverContext = !perms.is_owner && perms.can_act_on_stage;

    const openAction = (kind, requireComments = false) => {
        setActionDraft({ kind, comments: "", requireComments });
    };

    const submitAction = async () => {
        if (!actionDraft) return;
        const { kind, comments } = actionDraft;
        if ((kind === "reject" || kind === "clarify" || kind === "resubmit") && (comments || "").trim().length < 3) {
            toast.error("Comments are required (min 3 characters)");
            return;
        }
        try {
            const path = `/leave/request/${rid}/${kind}`;
            await api.post(path, { comments: comments || "" });
            toast.success(`${kind.replace(/^./, (c) => c.toUpperCase())} recorded`);
            setActionDraft(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, `${kind} failed`));
        }
    };

    const submitDraft = async () => {
        try {
            await api.post(`/leave/request/${rid}/submit`);
            toast.success("Submitted for approval");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Submit failed"));
        }
    };

    if (!req) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="leave-request-detail-loading">
                <PageHeader eyebrow="Leave Operations" title="Loading…" />
            </div>
        );
    }

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="leave-request-detail-page">
            <Button variant="ghost" size="sm" onClick={() => navigate("/leave/requests")} className="mb-2" data-testid="lrd-back-btn">
                <ArrowLeft size={14} className="mr-1" /> Back to Requests
            </Button>
            <PageHeader
                eyebrow={
                    inApproverContext
                        ? `Approval · ${req.leave_type_code} · ${req.policy_name || ""}`
                        : `${req.leave_type_code} · ${req.policy_name || ""}`
                }
                title={
                    inApproverContext
                        ? `Review · ${req.employee_name}`
                        : `${req.employee_name} · ${formatDate(req.from_date)} → ${formatDate(req.to_date)}`
                }
                description={
                    inApproverContext
                        ? `${formatDate(req.from_date)} → ${formatDate(req.to_date)} · ${req.days_total} working days · ${req.fiscal_year}`
                        : `${req.days_total} working days · ${req.fiscal_year} · ${req.reason}`
                }
                actions={(
                    <div className="flex flex-wrap gap-2 items-center">
                        <Badge className={`text-sm ${STATUS_TONE[req.status] || "bg-slate-100"}`} data-testid="lrd-status-badge">
                            {req.stage_label || req.status}
                        </Badge>
                        {canSubmitDraft && (
                            <Button size="sm" onClick={submitDraft} data-testid="lrd-submit-btn">
                                <PaperPlaneTilt size={14} className="mr-1" /> Submit for Approval
                            </Button>
                        )}
                        {canActOnStage && (
                            <>
                                <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300"
                                    onClick={() => openAction("approve")} data-testid="lrd-approve-btn">
                                    <CheckCircle size={14} className="mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" className="text-sky-700 border-sky-300"
                                    onClick={() => openAction("clarify", true)} data-testid="lrd-clarify-btn">
                                    <ChatCircle size={14} className="mr-1" /> Request Clarification
                                </Button>
                                <Button size="sm" variant="outline" className="text-rose-700 border-rose-300"
                                    onClick={() => openAction("reject", true)} data-testid="lrd-reject-btn">
                                    <XCircle size={14} className="mr-1" /> Reject
                                </Button>
                            </>
                        )}
                        {canResubmit && (
                            <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                                onClick={() => openAction("resubmit", true)} data-testid="lrd-resubmit-btn">
                                <ArrowsClockwise size={14} className="mr-1" /> Resubmit
                            </Button>
                        )}
                        {canCancel && (
                            <Button size="sm" variant="outline" className="text-zinc-700"
                                onClick={() => openAction("cancel")} data-testid="lrd-cancel-btn">
                                <Stop size={14} className="mr-1" /> Cancel
                            </Button>
                        )}
                    </div>
                )}
            />

            {/* Iter 109 · Awaiting Your Decision banner (B-06 · D-20).
                Renders ONLY for the current routed approver — never
                for future approvers, never for HR-with-override
                (they see the standard "HR override" affordance via
                the action buttons but no personalised urgency banner). */}
            {isCurrentApprover && canActOnStage && (
                <div
                    className="mt-4 rounded-xl border-l-4 border-amber-500 bg-amber-50 px-5 py-3 flex items-start gap-3"
                    data-testid="lrd-action-required-banner"
                    role="status"
                >
                    <Warning size={20} className="text-amber-700 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-amber-900">Awaiting your decision</div>
                        <div className="text-xs text-amber-800 mt-0.5">
                            {req.employee_name} has requested {req.days_total} working day(s) of {req.leave_type_code}
                            {" "}from {formatDate(req.from_date)} to {formatDate(req.to_date)}.
                            Review the decision-support panel below, then Approve, Reject, or Request Clarification.
                        </div>
                    </div>
                </div>
            )}
            {/* HR override affordance — separately labelled so HR
                knows their action is an override, not a routed
                decision (B-07). */}
            {!isCurrentApprover && perms.is_hr && canActOnStage && (
                <div
                    className="mt-4 rounded-xl border-l-4 border-sky-500 bg-sky-50 px-5 py-3 flex items-start gap-3"
                    data-testid="lrd-hr-override-banner"
                    role="status"
                >
                    <Warning size={20} className="text-sky-700 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-sky-900">HR override available</div>
                        <div className="text-xs text-sky-800 mt-0.5">
                            This request is currently with the {(perms.current_stage_role || "").replace(/_/g, " ") || "workflow"}.
                            You can act on it as an HR override — the action will be audit-logged separately.
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Iter 109 · Decision-Support panel (B-05 · D-23).
                    Rendered ONLY when the caller is the current routed
                    approver. Applicant + HR-not-current-approver see
                    None (persona-is-context rule). */}
                {isCurrentApprover && req.decision_support && (
                    <div
                        className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-5"
                        data-testid="lrd-decision-support"
                    >
                        <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <ChatCircle size={16} /> Decision Support
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
                            {/* (4) Applicant Leave Balance */}
                            <div className="bg-slate-50 rounded-lg p-3" data-testid="lrd-ds-balance">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Applicant Balance</div>
                                <div className="mt-1 text-base font-semibold text-slate-800">
                                    {req.decision_support.applicant_balance?.current_balance ?? 0}
                                    <span className="text-xs font-normal text-slate-500 ml-1">available</span>
                                </div>
                                <div className="text-slate-500 mt-0.5">
                                    {req.decision_support.applicant_balance?.leave_type_code} · requesting {req.decision_support.applicant_balance?.days_requested}
                                </div>
                            </div>
                            {/* (2) Duration + Half-day breakdown */}
                            <div className="bg-slate-50 rounded-lg p-3" data-testid="lrd-ds-duration">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Duration</div>
                                <div className="mt-1 text-base font-semibold text-slate-800">
                                    {req.decision_support.duration?.days_total} <span className="text-xs font-normal text-slate-500">day(s)</span>
                                </div>
                                <div className="text-slate-500 mt-0.5">
                                    First: {req.decision_support.duration?.first_half} · Last: {req.decision_support.duration?.last_half}
                                </div>
                            </div>
                            {/* (5) Previous Leave History */}
                            <div className="bg-slate-50 rounded-lg p-3" data-testid="lrd-ds-history">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Recent History (183d)</div>
                                <div className="mt-1 text-xs text-slate-800 space-y-0.5">
                                    <div>✓ {req.decision_support.leave_history_summary?.counts?.approved || 0} approved</div>
                                    <div>✕ {req.decision_support.leave_history_summary?.counts?.rejected || 0} rejected</div>
                                    <div>⊘ {req.decision_support.leave_history_summary?.counts?.cancelled || 0} cancelled</div>
                                    <div>⧗ {req.decision_support.leave_history_summary?.counts?.pending || 0} pending</div>
                                </div>
                            </div>
                            {/* P0-A Batch 2 · Provisional Paid/LOP split (advisory) */}
                            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3" data-testid="lrd-ds-provisional-split">
                                <div className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold">Provisional Split (advisory)</div>
                                <div className="mt-1 text-xs">
                                    <span className="font-semibold text-emerald-800">{Number(req.provisional_paid_days ?? req.days_total ?? 0).toFixed(2)}</span>
                                    <span className="text-slate-500"> Paid</span>
                                    <span className="mx-1 text-slate-400">·</span>
                                    <span className={`font-semibold ${Number(req.provisional_lop_days || 0) > 0 ? "text-amber-800" : "text-slate-400"}`}>{Number(req.provisional_lop_days ?? 0).toFixed(2)}</span>
                                    <span className="text-slate-500"> LOP</span>
                                </div>
                                <div className="mt-1 text-[9px] text-sky-800 italic">Authoritative split determined at payroll freeze</div>
                            </div>
                            {/* (6) Team overlap summary */}
                            <div className="bg-slate-50 rounded-lg p-3" data-testid="lrd-ds-overlap">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Team Overlap</div>
                                <div className="mt-1 text-base font-semibold text-slate-800">
                                    {req.decision_support.team_overlap?.count || 0}
                                    <span className="text-xs font-normal text-slate-500"> of {req.decision_support.team_overlap?.reports_total || 0}</span>
                                </div>
                                <div className={`mt-0.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                    (req.decision_support.team_overlap?.overlap_pct || 0) > 50 ? "bg-rose-100 text-rose-700" :
                                    (req.decision_support.team_overlap?.overlap_pct || 0) > 25 ? "bg-amber-100 text-amber-700" :
                                    "bg-emerald-100 text-emerald-700"
                                }`}>
                                    {req.decision_support.team_overlap?.overlap_pct || 0}% overlap
                                </div>
                            </div>
                        </div>
                        {/* (7) Employee remarks — promoted */}
                        {req.decision_support.employee_remarks && (
                            <div className="mt-3 border-t border-slate-100 pt-3" data-testid="lrd-ds-remarks">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Employee Remarks</div>
                                <div className="text-sm text-slate-700 italic">&ldquo;{req.decision_support.employee_remarks}&rdquo;</div>
                            </div>
                        )}
                    </div>
                )}

                {/* Workflow Timeline */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="lrd-workflow-section">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <ClockCounterClockwise size={16} /> Workflow Timeline
                    </h3>
                    {!req.workflow_snapshot && (
                        <div className="text-sm text-slate-500">No workflow yet — this is a Draft. Submit to freeze the approval chain.</div>
                    )}
                    {req.workflow_snapshot && (
                        <ol className="space-y-3">
                            {req.workflow_snapshot.stages.map((stage, idx) => {
                                const approved = (req.approvals || []).find((a) => a.stage_id === stage.id && a.action === "approve");
                                const rejected = (req.approvals || []).find((a) => a.stage_id === stage.id && a.action === "reject");
                                const clarified = (req.clarifications || []).filter((c) => c.stage_id === stage.id && c.action === "clarify");
                                const isCurrent = req.current_stage_id === stage.id;
                                const state = approved ? "approved" : rejected ? "rejected" : isCurrent ? "current" : "pending";
                                return (
                                    <li key={stage.id} className="flex gap-3" data-testid={`lrd-stage-${stage.id}`}>
                                        <div className={`mt-1 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
                                            ${state === "approved" ? "bg-emerald-100 text-emerald-700" :
                                              state === "rejected" ? "bg-rose-100 text-rose-700" :
                                              state === "current" ? "bg-amber-100 text-amber-700 ring-2 ring-amber-400" :
                                              "bg-slate-100 text-slate-500"}`}>
                                            {idx + 1}
                                        </div>
                                        <div className="flex-1 pb-3 border-b border-slate-100 last:border-0">
                                            <div className="flex items-center justify-between">
                                                <div className="font-medium text-sm text-slate-800">{stage.label || stage.role}</div>
                                                <Badge variant="outline" className="text-xs">{stage.role.replace(/_/g, " ")}</Badge>
                                            </div>
                                            {approved && (
                                                <div className="text-xs text-emerald-700 mt-1">
                                                    ✓ Approved by {approved.actor_name || approved.actor_email} · {formatDate(approved.at)}
                                                    {approved.comments && <div className="text-slate-500 italic mt-0.5">“{approved.comments}”</div>}
                                                </div>
                                            )}
                                            {rejected && (
                                                <div className="text-xs text-rose-700 mt-1">
                                                    ✕ Rejected by {rejected.actor_name || rejected.actor_email} · {formatDate(rejected.at)}
                                                    {rejected.comments && <div className="text-slate-500 italic mt-0.5">“{rejected.comments}”</div>}
                                                </div>
                                            )}
                                            {clarified.length > 0 && (
                                                <div className="mt-1 space-y-1">
                                                    {clarified.map((c, i) => (
                                                        <div key={i} className="text-xs text-sky-700">
                                                            ? Clarification by {c.actor_name || c.actor_email} · {formatDate(c.at)}
                                                            {c.comments && <div className="text-slate-500 italic mt-0.5">“{c.comments}”</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {state === "current" && (
                                                <div className="text-xs text-amber-700 mt-1">Awaiting decision…</div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}

                    {/* Resubmissions */}
                    {(req.clarifications || []).filter((c) => c.action === "resubmit").length > 0 && (
                        <div className="mt-4 pt-3 border-t border-slate-100">
                            <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">Resubmissions</h4>
                            <ul className="text-xs space-y-1">
                                {(req.clarifications || []).filter((c) => c.action === "resubmit").map((c, i) => (
                                    <li key={i} className="text-amber-800">
                                        ↻ {c.actor_email} · {formatDate(c.at)}
                                        {c.comments && <span className="text-slate-500 italic"> — “{c.comments}”</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Side panel: Team Calendar Awareness + Cancellation/Stamps */}
                <div className="space-y-4">
                {/* Iter 109 · Team-on-Leave panel — B-01/B-02.
                    Renders ONLY when server returns a non-null
                    `panel_context`. Applicant of the current request
                    receives `panel_context = null` and the whole
                    section is hidden. Approver context → "Direct
                    Reports". HR-review context → "Team Members". */}
                {team && team.panel_context && (
                    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="lrd-team-calendar">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                            <Users size={14} /> Team on Leave (this window)
                        </h4>
                        {(team.team_members || team.direct_reports || []).length === 0 && (team.department_peers || []).length === 0 && (
                            <div className="text-xs text-slate-400">No team members detected.</div>
                        )}
                        <div className="mb-3">
                            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
                                {team.panel_context === "reports_of_current_approver" ? "Your Direct Reports" : "Team Members"}
                            </div>
                            {(team.team_members || team.direct_reports || []).length === 0 ? (
                                <div className="text-xs text-slate-400 italic">No team members in scope.</div>
                            ) : (
                                (team.team_members || team.direct_reports || []).map((e) => (
                                    <div key={e.id} className="flex justify-between items-center text-xs py-1 border-b border-slate-100 last:border-0" data-testid={`lrd-dr-${e.id}`}>
                                        <div className="font-medium text-slate-800">{e.full_name}</div>
                                        <div>
                                            {e.requests.length > 0 ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{e.requests.length} leave</Badge>
                                            ) : (
                                                <span className="text-slate-400 text-[10px]">available</span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Department Peers</div>
                            {(team.department_peers || []).length === 0 ? (
                                <div className="text-xs text-slate-400 italic">No department peers.</div>
                            ) : (
                                team.department_peers.map((e) => (
                                    <div key={e.id} className="flex justify-between items-center text-xs py-1 border-b border-slate-100 last:border-0" data-testid={`lrd-peer-${e.id}`}>
                                        <div className="text-slate-700">{e.full_name}</div>
                                        <div>
                                            {e.requests.length > 0 ? (
                                                <Badge className="bg-amber-100 text-amber-700 text-[10px]">{e.requests.length} leave</Badge>
                                            ) : (
                                                <span className="text-slate-400 text-[10px]">available</span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="lrd-meta-card">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                            <CalendarBlank size={14} /> Request Metadata
                        </h4>
                        <dl className="text-xs space-y-1">
                            <div className="flex justify-between"><dt className="text-slate-500">Request ID</dt><dd className="font-mono">{req.id}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">Employee</dt><dd>{req.employee_name}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">Department</dt><dd>{req.department || "—"}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">First-Day</dt><dd>{req.first_half}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">Last-Day</dt><dd>{req.last_half}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">Working Days</dt><dd className="font-semibold">{req.days_total}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">Calendar Days</dt><dd>{req.calendar_days}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">LOP?</dt><dd>{req.is_lop ? "Yes" : "No"}</dd></div>
                            <div className="flex justify-between"><dt className="text-slate-500">Created</dt><dd>{formatDate(req.created_at)}</dd></div>
                            {req.submitted_at && (
                                <div className="flex justify-between"><dt className="text-slate-500">Submitted</dt><dd>{formatDate(req.submitted_at)}</dd></div>
                            )}
                            {req.decided_at && (
                                <div className="flex justify-between"><dt className="text-slate-500">Decided</dt><dd>{formatDate(req.decided_at)}</dd></div>
                            )}
                            {req.cancelled_at && (
                                <div className="flex justify-between"><dt className="text-slate-500">Cancelled</dt><dd>{formatDate(req.cancelled_at)}</dd></div>
                            )}
                            {req.ledger_entry_id && (
                                <div className="flex justify-between"><dt className="text-slate-500">Ledger Entry</dt><dd className="font-mono">{req.ledger_entry_id}</dd></div>
                            )}
                            {req.reversal_ledger_entry_id && (
                                <div className="flex justify-between"><dt className="text-slate-500">Reversal</dt><dd className="font-mono">{req.reversal_ledger_entry_id}</dd></div>
                            )}
                        </dl>
                    </div>
                </div>
            </div>

            {/* Action dialog */}
            <Dialog open={!!actionDraft} onOpenChange={(o) => !o && setActionDraft(null)}>
                <DialogContent data-testid="lrd-action-dialog">
                    <DialogHeader>
                        <DialogTitle>{actionDraft?.kind?.replace(/^./, (c) => c.toUpperCase())} this request?</DialogTitle>
                        <DialogDescription>
                            {actionDraft?.kind === "approve" && "Approve advances the workflow to the next stage. The final approval commits a ledger entry."}
                            {actionDraft?.kind === "reject" && "Rejection is terminal. Comments are required and visible to the requester."}
                            {actionDraft?.kind === "clarify" && "Pauses the request and notifies the employee to revisit. Comments are mandatory."}
                            {actionDraft?.kind === "resubmit" && "Returns the request to the clarifying stage with prior approvals preserved."}
                            {actionDraft?.kind === "cancel" && "Future-dated approved leaves can be cancelled by the owner up until the start date; HR can cancel anytime."}
                        </DialogDescription>
                    </DialogHeader>
                    {actionDraft && (
                        <div>
                            <label className="text-xs text-slate-600">
                                Comments{actionDraft.requireComments ? " (required, min 3 chars)" : ""}
                            </label>
                            <Textarea
                                data-testid="lrd-action-comments"
                                rows={3}
                                value={actionDraft.comments}
                                onChange={(e) => setActionDraft({ ...actionDraft, comments: e.target.value })}
                            />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setActionDraft(null)} data-testid="lrd-action-cancel">Cancel</Button>
                        <Button onClick={submitAction} data-testid="lrd-action-submit">
                            Confirm {actionDraft?.kind?.replace(/^./, (c) => c.toUpperCase())}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
